import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { API_URL } from '../../config/api';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../hooks/useTranslation';
import './css/AdminPages.css';

const defaultAllergenForm = {
  key_name: '',
  name_ar: '',
  name_en: '',
  icon: '',
  display_order: 0,
  is_active: true
};

const defaultAdditionForm = {
  name_ar: '',
  name_en: '',
  price: '',
  calories: '',
  display_order: 0,
  is_active: true
};

const AdminAdditions = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language, isRTL, toggleLanguage } = useLanguage();
  const [allergens, setAllergens] = useState([]);
  const [additions, setAdditions] = useState([]);
  const [allergenForm, setAllergenForm] = useState(defaultAllergenForm);
  const [additionForm, setAdditionForm] = useState(defaultAdditionForm);
  const [editingAllergenId, setEditingAllergenId] = useState(null);
  const [editingAdditionId, setEditingAdditionId] = useState(null);

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('adminToken')}` });

  const [loadError, setLoadError] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoadError('');
      const [allergensRes, additionsRes] = await Promise.all([
        axios.get(`${API_URL}/api/allergens/all`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/additions/all`, { headers: getHeaders() })
      ]);
      setAllergens(allergensRes.data || []);
      setAdditions(additionsRes.data || []);
    } catch (error) {
      console.error('Error loading allergens/additions:', error);
      setLoadError(error.response?.data?.message || '__LOAD_ERROR__');
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const submitAllergen = async (e) => {
    e.preventDefault();
    try {
      if (editingAllergenId) {
        await axios.put(`${API_URL}/api/allergens/${editingAllergenId}`, allergenForm, { headers: getHeaders() });
      } else {
        await axios.post(`${API_URL}/api/allergens`, allergenForm, { headers: getHeaders() });
      }
      setAllergenForm(defaultAllergenForm);
      setEditingAllergenId(null);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.message || t('admin.errorSavingAllergen'));
    }
  };

  const submitAddition = async (e) => {
    e.preventDefault();
    try {
      if (editingAdditionId) {
        await axios.put(`${API_URL}/api/additions/${editingAdditionId}`, additionForm, { headers: getHeaders() });
      } else {
        await axios.post(`${API_URL}/api/additions`, additionForm, { headers: getHeaders() });
      }
      setAdditionForm(defaultAdditionForm);
      setEditingAdditionId(null);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.message || t('admin.errorSavingAddition'));
    }
  };

  return (
    <div className={`admin-page ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="admin-header">
        <button onClick={() => navigate('/admin/dashboard')} className="back-btn">
          <ArrowLeftIcon className="icon" />
          {t('admin.back')}
        </button>
        <h1>{t('admin.allergensAdditions')}</h1>
        <div className="header-actions">
          <button onClick={toggleLanguage} className="language-toggle-btn">
            {language === 'ar' ? 'EN' : 'AR'}
          </button>
        </div>
      </div>

      <div className="admin-table-container" style={{ marginBottom: '20px' }}>
        {loadError ? (
          <div style={{ marginBottom: '12px', color: '#b91c1c', fontWeight: 600 }}>
            {loadError === '__LOAD_ERROR__' ? t('admin.errorLoadingData') : loadError}
          </div>
        ) : null}
        <h2 style={{ marginBottom: '10px' }}>{t('admin.manageAllergens')}</h2>
        <form onSubmit={submitAllergen}>
          <div className="form-group"><label>{t('admin.key')}</label><input value={allergenForm.key_name} onChange={(e) => setAllergenForm({ ...allergenForm, key_name: e.target.value })} /></div>
          <div className="form-group"><label>{t('admin.nameAr')} *</label><input required value={allergenForm.name_ar} onChange={(e) => setAllergenForm({ ...allergenForm, name_ar: e.target.value })} /></div>
          <div className="form-group"><label>{t('admin.nameEn')} *</label><input required value={allergenForm.name_en} onChange={(e) => setAllergenForm({ ...allergenForm, name_en: e.target.value })} /></div>
          <div className="form-group"><label>{t('admin.icon')}</label><input value={allergenForm.icon} onChange={(e) => setAllergenForm({ ...allergenForm, icon: e.target.value })} placeholder="🥜" /></div>
          <div className="form-group"><label>{t('admin.order')}</label><input type="number" value={allergenForm.display_order} onChange={(e) => setAllergenForm({ ...allergenForm, display_order: parseInt(e.target.value, 10) || 0 })} /></div>
          <div className="form-group"><label><input type="checkbox" checked={allergenForm.is_active} onChange={(e) => setAllergenForm({ ...allergenForm, is_active: e.target.checked })} /> {t('admin.active')}</label></div>
          <button type="submit" className="save-btn">{editingAllergenId ? t('admin.update') : t('admin.add')} {t('admin.allergen')}</button>
        </form>
        <table className="admin-table" style={{ marginTop: '14px' }}>
          <thead><tr><th>{t('admin.id')}</th><th>{t('admin.nameAr')}</th><th>{t('admin.nameEn')}</th><th>{t('admin.icon')}</th><th>{t('admin.active')}</th><th>{t('admin.actions')}</th></tr></thead>
          <tbody>
            {allergens.map((item) => (
              <tr key={item.id}>
                <td>{item.id}</td>
                <td>{item.name_ar}</td>
                <td>{item.name_en}</td>
                <td>{item.icon || '-'}</td>
                <td>{item.is_active ? t('admin.yes') : t('admin.no')}</td>
                <td>
                  <button className="edit-btn" onClick={() => { setEditingAllergenId(item.id); setAllergenForm({ ...item }); }}>{t('admin.edit')}</button>
                  <button className="delete-btn" onClick={async () => { if (window.confirm(t('admin.deleteAllergenConfirm'))) { await axios.delete(`${API_URL}/api/allergens/${item.id}`, { headers: getHeaders() }); fetchData(); } }}>{t('admin.delete')}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="admin-table-container">
        <h2 style={{ marginBottom: '10px' }}>{t('admin.manageFixedAdditions')}</h2>
        <form onSubmit={submitAddition}>
          <div className="form-group"><label>{t('admin.nameAr')} *</label><input required value={additionForm.name_ar} onChange={(e) => setAdditionForm({ ...additionForm, name_ar: e.target.value })} /></div>
          <div className="form-group"><label>{t('admin.nameEn')} *</label><input required value={additionForm.name_en} onChange={(e) => setAdditionForm({ ...additionForm, name_en: e.target.value })} /></div>
          <div className="form-group"><label>{t('admin.price')} *</label><input required type="number" step="0.01" value={additionForm.price} onChange={(e) => setAdditionForm({ ...additionForm, price: e.target.value })} /></div>
          <div className="form-group"><label>{t('admin.calories')}</label><input type="number" value={additionForm.calories} onChange={(e) => setAdditionForm({ ...additionForm, calories: e.target.value })} /></div>
          <div className="form-group"><label>{t('admin.order')}</label><input type="number" value={additionForm.display_order} onChange={(e) => setAdditionForm({ ...additionForm, display_order: parseInt(e.target.value, 10) || 0 })} /></div>
          <div className="form-group"><label><input type="checkbox" checked={additionForm.is_active} onChange={(e) => setAdditionForm({ ...additionForm, is_active: e.target.checked })} /> {t('admin.active')}</label></div>
          <button type="submit" className="save-btn">{editingAdditionId ? t('admin.update') : t('admin.add')} {t('admin.addition')}</button>
        </form>
        <table className="admin-table" style={{ marginTop: '14px' }}>
          <thead><tr><th>{t('admin.id')}</th><th>{t('admin.nameAr')}</th><th>{t('admin.nameEn')}</th><th>{t('admin.price')}</th><th>{t('admin.calories')}</th><th>{t('admin.active')}</th><th>{t('admin.actions')}</th></tr></thead>
          <tbody>
            {additions.map((item) => (
              <tr key={item.id}>
                <td>{item.id}</td>
                <td>{item.name_ar}</td>
                <td>{item.name_en}</td>
                <td>{item.price}</td>
                <td>{item.calories || 0}</td>
                <td>{item.is_active ? t('admin.yes') : t('admin.no')}</td>
                <td>
                  <button className="edit-btn" onClick={() => { setEditingAdditionId(item.id); setAdditionForm({ ...item }); }}>{t('admin.edit')}</button>
                  <button className="delete-btn" onClick={async () => { if (window.confirm(t('admin.deleteAdditionConfirm'))) { await axios.delete(`${API_URL}/api/additions/${item.id}`, { headers: getHeaders() }); fetchData(); } }}>{t('admin.delete')}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminAdditions;
